import * as React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../providers/AuthProvider'
import DiagSpinner from '../components/diagnostics/DiagSpinner'

/**
 * Require authentication to access child routes
 * Shows loading spinner while auth initializes
 * Redirects to /login if not authenticated
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { authenticated, initialized } = useAuth()
  const location = useLocation()

  // Show loading while auth initializes
  if (!initialized) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 12,
      }}>
        <DiagSpinner size="large" />
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Checking authentication...
        </span>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!authenticated) {
    return <Navigate to="/login" state={{ from: `${location.pathname}${location.search}${location.hash}` }} replace />
  }

  return <>{children}</>
}

/**
 * Require specific roles to access child routes
 * Redirects to /unauthorized if user lacks required roles
 * 
 * @param allow - Array of role names (at least one required)
 */
export function RequireRole({
  children,
  allow,
}: {
  children: React.ReactNode
  allow: string[]
}) {
  const { roles } = useAuth()

  // Check if user has any of the required roles
  const allowed = allow.some((role) => roles.includes(role))

  if (!allowed) {
    return <Navigate to="/unauthorized" replace />
  }

  return <>{children}</>
}

